import { useEffect, useMemo, useState } from 'react';
import type { Tenant } from '../../lib/database.types';
import { loadNetworkCustomers } from '../../lib/networkCrmService';
import type { NetworkCustomer } from '../../lib/networkCrm';
import {
  selectRecipients,
  fillTemplate,
  waMeLink,
  isCampaignReady,
  AUDIENCE_LABELS,
  type Audience,
  type CampaignRow,
} from '../../lib/whatsapp';
import { saveCampaign, loadCampaigns } from '../../lib/whatsappService';

/**
 * Section « WhatsApp Business » du SuperAdmin (brique G) : préparer une diffusion vers
 * la base clients opt-in (réseau ou un resto), message personnalisé {prenom}, aperçu,
 * nombre de destinataires, enregistrement de la campagne + liens wa.me.
 * L'envoi en masse passera par l'API WhatsApp Business Cloud (intégration Meta séparée).
 */
export default function WhatsAppSection({ tenants }: { tenants: Tenant[] }) {
  const [customers, setCustomers] = useState<NetworkCustomer[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('Bonjour {prenom}, ');
  const [audience, setAudience] = useState<Audience>('network');
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([loadNetworkCustomers(), loadCampaigns()]).then(([cust, camps]) => {
      if (cust.ok) setCustomers(cust.customers);
      else setError(cust.error ?? 'Chargement impossible.');
      if (camps.ok) setCampaigns(camps.campaigns);
      setLoading(false);
    });
  }, []);

  const recipients = useMemo(
    () => selectRecipients(customers, audience, tenantId),
    [customers, audience, tenantId],
  );
  const ready = isCampaignReady({ title, message, audience, tenantId }, recipients.length);
  const sample = recipients[0];

  const tenantName = (id: string | null) => tenants.find((t) => t.id === id)?.name ?? '—';

  const save = async () => {
    setSaving(true);
    setFeedback(null);
    const res = await saveCampaign({ title: title.trim(), message: message.trim(), audience, tenantId, recipientCount: recipients.length });
    setSaving(false);
    if (res.ok) {
      setFeedback(`✅ Campagne enregistrée (${recipients.length} destinataires).`);
      const reload = await loadCampaigns();
      if (reload.ok) setCampaigns(reload.campaigns);
    } else {
      setFeedback('⚠️ ' + res.error);
    }
  };

  if (error) {
    return (
      <div className="max-w-2xl">
        <h1 className="text-xl font-bold mb-1">WhatsApp Business</h1>
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700 text-sm">
          ⚠️ {error}
          <div className="opacity-80 mt-1">
            Vérifie que <code>sql/28_whatsapp.sql</code> est lancé et que ton compte est <code>super_admin</code>.
          </div>
        </div>
      </div>
    );
  }
  if (loading) return <p className="opacity-70">Chargement de la base clients…</p>;

  return (
    <div className="max-w-3xl">
      <div
        className="rounded-xl p-4 mb-4 text-white"
        style={{ background: 'linear-gradient(135deg, #128C7E 0%, #25D366 100%)' }}
      >
        <div className="text-xs uppercase font-bold opacity-80">💬 WhatsApp Business</div>
        <div className="text-lg font-extrabold">Diffuser à ta base clients opt-in</div>
        <div className="text-sm opacity-90">
          Prépare le message et la liste. L'envoi en masse via l'API WhatsApp Business Cloud (Meta) arrive ensuite.
        </div>
      </div>

      {/* Cible */}
      <label className="block text-sm font-semibold mb-1">Audience</label>
      <div className="flex flex-wrap gap-2 mb-3 items-center">
        <select
          value={audience}
          onChange={(e) => setAudience(e.target.value as Audience)}
          className="rounded border border-slate-300 px-2 py-1.5 text-sm"
        >
          <option value="network">{AUDIENCE_LABELS.network}</option>
          <option value="tenant">{AUDIENCE_LABELS.tenant}</option>
        </select>
        {audience === 'tenant' && (
          <select
            value={tenantId ?? ''}
            onChange={(e) => setTenantId(e.target.value || null)}
            className="rounded border border-slate-300 px-2 py-1.5 text-sm"
          >
            <option value="">Choisir un resto…</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        )}
        <span className="text-sm font-semibold rounded-full bg-green-100 text-green-800 px-3 py-1">
          {recipients.length} destinataire(s)
        </span>
      </div>

      <label className="block text-sm font-semibold mb-1">Titre (interne)</label>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Ex. Promo week-end mars"
        className="w-full rounded border border-slate-300 px-3 py-2 mb-3 text-sm"
      />

      <label className="block text-sm font-semibold mb-1">
        Message <span className="opacity-50 font-normal">(variables : {'{prenom}'}, {'{nom}'})</span>
      </label>
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={3}
        className="w-full rounded border border-slate-300 px-3 py-2 mb-2 text-sm"
      />

      {/* Aperçu */}
      <div className="mb-4">
        <div className="text-xs uppercase font-bold opacity-60 mb-1">Aperçu (1er destinataire)</div>
        <div className="rounded-lg bg-[#DCF8C6] text-slate-800 px-3 py-2 text-sm max-w-md">
          {sample ? fillTemplate(message, sample) : '(aucun destinataire pour cette cible)'}
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap mb-2">
        <button
          type="button"
          disabled={saving || !ready}
          onClick={save}
          className="rounded-md px-4 py-2 text-white font-semibold disabled:opacity-40"
          style={{ background: '#25D366' }}
        >
          {saving ? 'Enregistrement…' : 'Enregistrer la campagne'}
        </button>
        {sample && (
          <a
            href={waMeLink(sample.phone, fillTemplate(message, sample))}
            target="_blank"
            rel="noreferrer"
            className="text-sm underline text-green-700"
          >
            Tester avec {sample.firstName} (wa.me) ↗
          </a>
        )}
        {feedback && <span className="text-sm">{feedback}</span>}
      </div>
      <p className="text-xs opacity-50 mb-5">
        Les destinataires sont les clients ayant accepté la communication ({audience === 'network' ? 'réseau' : 'du resto'}).
        Respecte le consentement et propose toujours le désabonnement.
      </p>

      {/* Historique */}
      <h2 className="text-sm font-bold uppercase opacity-60 mb-2">Campagnes enregistrées</h2>
      {campaigns.length === 0 ? (
        <p className="text-sm opacity-50">Aucune campagne pour l'instant.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {campaigns.map((c) => (
            <li key={c.id} className="rounded-lg bg-white border border-slate-200 p-3 text-sm">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="font-semibold">{c.title || '(sans titre)'}</span>
                <span className="text-xs opacity-60">
                  {c.audience === 'tenant' ? tenantName(c.tenantId) : 'Réseau'} · {c.recipientCount} dest. · {c.status}
                </span>
              </div>
              <div className="opacity-70 text-xs mt-1">{c.message}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
